import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import {
  BarChart3, TrendingUp, Cloud, Crosshair, Users, FileText, MapPin, Trophy
} from 'lucide-react';

const features = [
  {
    icon: BarChart3,
    title: 'Score en Direct',
    desc: 'Enregistrez vos scores en temps réel pendant la session. Onglets par tireur, grille de saisie par plateau, support multi-tireurs avec tirage au sort.',
    span: 'lg:col-span-1',
  },
  {
    icon: TrendingUp,
    title: 'Analyses Détaillées',
    desc: 'Graphiques de progression par discipline. Analyse Pull vs Mark en Skeet, moyennes par série, par poste, par période. Tendances de progression.',
    span: 'lg:col-span-1',
  },
  {
    icon: Cloud,
    title: 'Météo Intégrée',
    desc: 'Météo en temps réel avec vent, visibilité, conditions de tir. Conseils adaptés et indicateur Idéal / Modéré / Difficile / Défavorable.',
    span: 'lg:col-span-1',
  },
  {
    icon: Crosshair,
    title: 'Arsenal',
    desc: 'Gérez vos fusils : marque, modèle, calibre, chokes. Base de données de 30+ marques. Suivi des cartouches et corrélation performance/équipement.',
    span: 'lg:col-span-1',
  },
  {
    icon: Users,
    title: 'Sessions Invité',
    desc: "Enregistrez les scores d'un ami. Analyse détaillée par invité avec PDF partageable. Carte résumé élégante, données auto-supprimées après 24h.",
    span: 'lg:col-span-1',
  },
  {
    icon: FileText,
    title: 'Licence Numérique',
    desc: 'Importez votre licence FFBT/FFT depuis un PDF. Extraction automatique : nom, club, numéro de licence, saison. Carte virtuelle toujours accessible.',
    span: 'lg:col-span-1',
  },
  {
    icon: MapPin,
    title: 'Lieux de Tir',
    desc: 'Répertoire de stands de ball-trap partenaires. Géolocalisation et informations pratiques. Filtres par région et discipline.',
    span: 'lg:col-span-1',
  },
  {
    icon: Trophy,
    title: 'Multi-Tireurs',
    desc: "Jusqu'à 6 tireurs simultanés. Onglets horizontaux ergonomiques. Tirage au sort animé de l'ordre de passage avec verrouillage.",
    span: 'lg:col-span-1',
  },
];

function FeatureCard({ feature, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const Icon = feature.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      data-testid={`feature-card-${index}`}
      className={`${feature.span} group bg-[#1A1A1A] rounded-2xl p-6 border border-white/5 hover:border-[#FF6B35]/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(255,107,53,0.08)]`}
    >
      <div className="w-12 h-12 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center mb-4 group-hover:bg-[#FF6B35]/20 transition-colors duration-300">
        <Icon size={24} className="text-[#FF6B35]" />
      </div>
      <h3 className="text-white font-['Poppins'] font-bold text-lg mb-2">{feature.title}</h3>
      <p className="text-[#A0A0A0] text-sm leading-relaxed">{feature.desc}</p>
    </motion.div>
  );
}

export default function Features() {
  return (
    <section id="fonctionnalites" data-testid="features-section" className="py-24 sm:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-[#FF6B35] text-sm font-semibold uppercase tracking-widest font-['Poppins']"
          >
            Fonctionnalités
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white font-['Poppins'] font-bold text-3xl sm:text-4xl mt-3"
          >
            Tout ce dont vous avez besoin
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-[#A0A0A0] mt-4 max-w-xl mx-auto text-base"
          >
            Des outils puissants pour suivre, analyser et améliorer vos performances au ball-trap.
          </motion.p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, i) => (
            <FeatureCard key={i} feature={feature} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
